import {
  Component,
  computed,
  signal,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom, map } from 'rxjs';
import { PlantStatus, PlantStatusEnum } from '@anessa/shared-models';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProjectService } from '../../shared/services/project/project.service';
import { Plant } from '../dashboard/services/plant/plant.model';
import { Project } from '../../shared/services/project/project.model';
import { MatButtonModule } from '@angular/material/button';
import { MyAuthService } from '../../auth/auth.service';
import { LoadingSpinner } from '../../shared/common/loading-spinner/loading-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../shared/services/permissions-management/user.service';
import { RoleService } from '../../shared/services/permissions-management/role.service';
import { UserPlantRoleService } from '../../shared/services/permissions-management/user-plant-role.service';
import { Role } from '../../shared/interfaces/role.interface';
import { UserPlantRole } from '../../shared/interfaces/user-plant-role.interface';
import { MatTooltip } from '@angular/material/tooltip';
import { ErrorService } from '../../shared/services/error/error.service';

const ERROR_SOURCE = 'ProjectSelection';

type StatusFilterOptions = PlantStatus | 'All';

interface ProjectListItem {
  id: string;
  name: string;
  isHealthLoading: boolean;
  status: PlantStatus | null;
  lastData: string;
  lastDataSeconds: number | null;
  role: string;
  favorite: boolean;
  plantMeta?: Plant;
}

@Component({
  selector: 'app-project-selection',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    I18nPipe,
    I18nScopeReadyDirective,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    LoadingSpinner,
    MatProgressSpinnerModule,
    FormsModule,
    MatTooltip,
  ],
  templateUrl: './project-selection.html',
  styleUrl: './project-selection.scss',
  providers: [
    provideI18nScope('features/project-selection'),
    I18nScopedService,
  ],
})
export class ProjectSelection implements OnInit {
  readonly i18n = inject(I18nScopedService);
  readonly projectService = inject(ProjectService);
  readonly scopeReady = this.i18n.getScopeReady();

  private readonly userService = inject(UserService);
  private readonly roleService = inject(RoleService);
  private readonly userPlantRoleService = inject(UserPlantRoleService);
  private readonly authService = inject(MyAuthService);
  private readonly errorService = inject(ErrorService);

  private readonly allProjects = signal<ProjectListItem[]>([]);

  plantStatusEnum = PlantStatusEnum;

  searchQuery = signal('');
  showFavoritesOnly = signal(false);
  statusFilter = signal<StatusFilterOptions | null>(null);
  allRoles = signal<Role[]>([]);
  selectedRoles = signal<Role[]>([]);
  private readonly plantRoleMap = signal<Map<string, string>>(new Map()); // plantId -> role.name
  showRoleDropdown = signal(false);
  lastDataSortAsc = signal(true);
  // for now, we consider a project viewable if the user has any role for its plant
  viewableProjects = computed(() =>
    this.allProjects().filter((project) =>
      this.plantRoleMap().has(project.plantMeta?.id ?? ''),
    ),
  );
  filterSearch = '';

  constructor() {
    this.projectService
      .getProjectsCachedWithRefresh({ plantInclude: ['health'] })
      .pipe(takeUntilDestroyed())
      .pipe(map((project) => project.map((p) => this.toProjectListItem(p))))
      .subscribe((projectListItems) =>
        this.allProjects.set(this.applyRoles(projectListItems)),
      );
  }

  public onClearFilters() {
    this.filterSearch = '';
    this.searchQuery.set('');
    this.selectedRoles.set([]);
    this.statusFilter.set(null);
  }

  ngOnInit(): void {
    this.initialize();
  }

  async initialize(): Promise<void> {
    await this.fetchRoles();
    await this.fetchCurrUserRoles();
  }

  async fetchRoles() {
    try {
      const fetchedRoles = await firstValueFrom(this.roleService.getAllRoles());
      if (!fetchedRoles) return;

      this.allRoles.set(fetchedRoles);
      this.selectedRoles.set([]);
    } catch (err) {
      this.errorService.report(err, {
        source: ERROR_SOURCE,
        action: 'fetchRoles',
      });
    }
  }

  async fetchCurrUserRoles() {
    try {
      const auth0User = await firstValueFrom(this.authService.user$);
      const auth0Id = auth0User?.sub;

      if (!auth0Id) return;

      const user = await firstValueFrom(
        this.userService.getUserbyAuth0Id(auth0Id),
      );

      const userId = user?._id;
      if (!userId) {
        this.errorService.report(new Error('No user found for this Auth0 ID'), {
          source: ERROR_SOURCE,
          action: 'fetchCurrUserRoles',
          metadata: { auth0Id },
        });
        return;
      }

      const userRoleAssignments = await firstValueFrom(
        this.userPlantRoleService.getUserRoles(userId),
      );

      userRoleAssignments.forEach((assignment: UserPlantRole) => {
        const roleMatch = this.allRoles().find(
          (r) => r._id === assignment.roleId,
        );
        if (roleMatch) {
          this.plantRoleMap.update((map) =>
            new Map(map).set(assignment.plantId, roleMatch.name),
          );
        }
      });

      this.allProjects.update((currProjects: ProjectListItem[]) =>
        this.applyRoles(currProjects),
      );
    } catch (err) {
      this.errorService.report(err, {
        source: ERROR_SOURCE,
        action: 'fetchCurrUserRoles',
      });
    }
  }

  applyRoles(projects: ProjectListItem[]): ProjectListItem[] {
    return projects.map((project) => ({
      ...project,
      role: this.plantRoleMap().get(project.plantMeta?.id ?? '') ?? '-',
    }));
  }

  filteredProjects = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const status = this.statusFilter();
    const roleNames = this.selectedRoles().map((role) => role.name);
    const asc = this.lastDataSortAsc();

    return this.viewableProjects()
      .filter(
        (p) =>
          p.name.toLowerCase().includes(query) &&
          (!this.showFavoritesOnly() || p.favorite) &&
          (!status || status === 'All' || p.status === status) &&
          (roleNames.length === 0 || roleNames.includes(p.role)),
      )
      .sort((a, b) => {
        const aVal = a.lastDataSeconds ?? Number.POSITIVE_INFINITY;
        const bVal = b.lastDataSeconds ?? Number.POSITIVE_INFINITY;

        return asc ? aVal - bVal : bVal - aVal;
      });
  });

  updateStatusFilter(status: PlantStatus | null) {
    this.statusFilter.set(status ?? 'All');
  }

  updateRolesFilter(roles: Role[]) {
    this.selectedRoles.set(roles);
  }

  toggleLastDataSort(): void {
    this.lastDataSortAsc.update((v) => !v);
  }

  toggleFavorite(event: Event, plant: ProjectListItem): void {
    event.stopPropagation();

    this.allProjects.update((plants) =>
      plants.map((p) =>
        p.name === plant.name ? { ...p, favorite: !p.favorite } : p,
      ),
    );
  }

  toggleFavoritesFilter(): void {
    this.showFavoritesOnly.update((v) => !v);
  }

  toggleRole(role: Role): void {
    this.selectedRoles.update((roles) =>
      roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role],
    );
  }

  isRoleSelected(role: Role): boolean {
    return this.selectedRoles().includes(role);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.role-dropdown-wrapper')) {
      this.showRoleDropdown.set(false);
    }
  }

  private toProjectListItem(p: Project): ProjectListItem {
    const isHealthLoading = !!p.plantMeta && !p.plantMeta.health;
    // TODO: favorites being saved and so forth is a whole thing...
    return {
      id: p.id,
      name: p.projectName ?? '',
      isHealthLoading,
      status: p.plantMeta?.health?.status ?? null,
      lastData: p.plantMeta?.lastDataDateString ?? '',
      lastDataSeconds: p.plantMeta?.lastDataInSeconds ?? null,
      role: '-',
      favorite: false,
      plantMeta: p.plantMeta,
    };
  }

  openPlant(projectListItem: ProjectListItem) {
    this.projectService.openProject(projectListItem.id);
  }
}
